import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createStep,
  deleteStep,
  insertProjectReference,
  refreshFromSource,
  updateStep,
} from '@/api/steps'
import { PROJECTS_QUERY_KEY } from './use-projects'
import {
  appendStepToTree,
  isStructuralStepUpdate,
  patchStepInTree,
  removeStepFromTree,
} from './cache-transforms'
import type { CreateStepRequest, ProjectDetail, StepNode, UpdateStepRequest } from '@/api/types'

/**
 * Patch every cached {@link ProjectDetail} for `projectId` regardless of which query key holds
 * it — `['projects', 'detail', id]` (id-route) AND `['projects', 'by-handle-slug', handle, slug]`
 * (canonical /p/:handle/:slug route) both hold the same shape, and the page might be subscribed
 * to either one. setQueriesData with a predicate keeps optimistic patches in sync across both.
 */
const patchAllProjectDetailCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
  transform: (detail: ProjectDetail) => ProjectDetail
) => {
  queryClient.setQueriesData<ProjectDetail | undefined>(
    {
      predicate: (query) => {
        if (query.queryKey[0] !== 'projects') return false
        const data = query.state.data as ProjectDetail | undefined
        return data?.id === projectId
      },
    },
    (data) => (data ? transform(data) : data)
  )
}

export const useCreateStep = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateStepRequest) => createStep(projectId, data),
    // Patch every cached ProjectDetail with the server's response directly — the outline
    // re-renders the instant the mutation resolves, no waiting on invalidate-then-refetch.
    // Broad PROJECTS_QUERY_KEY invalidate runs afterwards as a belt-and-suspenders guard.
    onSuccess: (newStep: StepNode) => {
      patchAllProjectDetailCaches(queryClient, projectId, (detail) =>
        appendStepToTree(detail, newStep)
      )
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY })
    },
  })
}

type ProjectDetailSnapshot = [readonly unknown[], ProjectDetail][]

/** Snapshot every cached ProjectDetail for `projectId` so an optimistic patch can be rolled back. */
const snapshotProjectDetailCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string
): ProjectDetailSnapshot => {
  const out: ProjectDetailSnapshot = []
  queryClient
    .getQueryCache()
    .findAll({
      predicate: (query) => {
        if (query.queryKey[0] !== 'projects') return false
        const d = query.state.data as ProjectDetail | undefined
        return d?.id === projectId
      },
    })
    .forEach((query) => {
      out.push([query.queryKey, query.state.data as ProjectDetail])
    })
  return out
}

const restoreProjectDetailCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot: ProjectDetailSnapshot
) => {
  snapshot.forEach(([key, data]) => queryClient.setQueryData(key, data))
}

interface UpdateStepCtx {
  snapshot: ProjectDetailSnapshot
}

export const useUpdateStep = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, { stepId: string; data: UpdateStepRequest }, UpdateStepCtx>({
    mutationFn: ({ stepId, data }) => updateStep(stepId, data),
    // Optimistic: only for non-structural patches (title / description). Tree restructuring
    // (sortOrder, parentStepId, clearParent) goes through invalidate-then-refetch.
    onMutate: async ({ stepId, data }) => {
      if (isStructuralStepUpdate(data)) return { snapshot: [] }
      await queryClient.cancelQueries({ queryKey: PROJECTS_QUERY_KEY })
      const snapshot = snapshotProjectDetailCaches(queryClient, projectId)
      patchAllProjectDetailCaches(queryClient, projectId, (detail) =>
        patchStepInTree(detail, stepId, data)
      )
      return { snapshot }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) restoreProjectDetailCaches(queryClient, ctx.snapshot)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY })
    },
  })
}

export const useDeleteStep = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (stepId: string) => deleteStep(stepId),
    onSuccess: (_data, stepId) => {
      patchAllProjectDetailCaches(queryClient, projectId, (detail) =>
        removeStepFromTree(detail, stepId)
      )
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY })
    },
  })
}

// projectId kept in the signature for API stability with callers; the broad
// PROJECTS_QUERY_KEY invalidate doesn't need it. `void` marks it as intentionally
// referenced so TypeScript's noUnusedParameters stays quiet.
export const useInsertProjectReference = (projectId: string) => {
  void projectId
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ stepId, sourceProjectId }: { stepId: string; sourceProjectId: string }) =>
      insertProjectReference(stepId, { sourceProjectId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY })
    },
  })
}

export const useRefreshFromSource = (projectId: string) => {
  void projectId
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (stepId: string) => refreshFromSource(stepId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY })
    },
  })
}
